import { Base, FetchingServices } from '@/composables/base';
import { reactive } from 'vue';
import { parseJwtPayload } from '~/types/permission/Types';
import { useSettingsDepartment } from '../department';

export enum UserType {
  NAVA_NUMBER = 'navanumber',
  NAVA_USER = 'navauser',
  IP_PHONE = 'ipphone',
}

//  |
//  |   User Types
// @@@
//  @
export type TUserModel = {
  data: string;
  name: string;
  family: string | null;
  password: string;
  permissions: string;
  number: string;
  role: string;
  type: string;
  uuid: string;
  departmentName?: string;
  user?: string;
};

interface IUserTokenData {
  adphone?: string;
  adsub?: string;
  aud?: string;
  dtkn?: string;
  email?: string;
  exp?: number;
  iat?: number;
  nickname?: string;
  phone?: string;
  sub?: string;
}

//  |
//  |   Filter Criterias type
// @@@
//  @
type TFilterCriterias = {
  page_number: number;
  page_size: number;
};

//  |
//  |   Interface and Class
// @@@
//  @
interface IUserApi {
  // api
  getUserListApi(): void;
}
interface IUserMethods {
  // getter
  getUserId(): string | null;

  // public
  getUsers(): void;
  populateUserDepartment(updateDepartments: boolean): void;
  requireUser: (user: IUserTokenData | undefined) => user is IUserTokenData;
}
interface IUser extends IUserApi, IUserMethods {
  user: IUserTokenData | undefined;
  isOwner: boolean;
  userList: TUserModel[];
}
export class User extends Base implements IUser {
  user: IUserTokenData | undefined;
  isOwner: boolean = true;
  userList: TUserModel[] = [];

  filterCriterias: TFilterCriterias = {
    page_number: 1,
    page_size: 10,
  };

  async getUserListApi() {
    try {
      const [resNavaNumbers, resNavaUsers, resIpPhones] = await Promise.all([
        this.get<TUserModel[]>(
          FetchingServices.ipbx,
          'nnava-number'
          // 'subusers': what is this api for? when api above returns all users. TODO: ask about this
        ),
        this.get<TUserModel[]>(
          FetchingServices.ipbx,
          'nava-accounts'
          // 'subusers': what is this api for? when api above returns all users. TODO: ask about this
        ),
        this.get<TUserModel[]>(
          FetchingServices.ipbx,
          'accounts'
          // 'subusers': what is this api for? when api above returns all users. TODO: ask about this
        ),
      ]);

      // clear the user list
      this.userList = [];

      // Navatel Number users
      if (resNavaNumbers.data) {
        resNavaNumbers.data.forEach((u) => {
          let name = '';
          if (u.name && u.family) name = u.name + ' ' + u.family;
          else name = u.user || u.number || '';

          this.userList.push({
            ...u,
            type: UserType.NAVA_NUMBER,
            number: u.number?.trim(),
            name,
            departmentName: '',
          });
        });
      }

      // Navatel Users
      if (resNavaUsers.data) {
        resNavaUsers.data.forEach((u) => {
          if (!u.user) {
            console.log('User has no userName!');
            return;
          }
          let name = '';
          if (u.name && u.family) name = u.name + ' ' + u.family;
          else name = u.user || u.number || '';

          this.userList.push({
            ...u,
            type: UserType.NAVA_USER,
            number: u.user?.trim(),
            name,
            departmentName: '',
          });
        });
      }

      // Navatel Ip Phones
      if (resIpPhones.data) {
        resIpPhones.data.forEach((u) => {
          if (!u.user) {
            console.log('User has no userName!');
            return;
          }
          let name = '';
          if (u.name && u.family) name = u.name + ' ' + u.family;
          else name = u.user || u.number || '';

          this.userList.push({
            ...u,
            type: UserType.IP_PHONE,
            number: u.user?.trim(),
            name,
            departmentName: '',
          });
        });
      }

      // remove duplicates
      this.userList = this.userList.filter(
        (user, index, self) =>
          index === self.findIndex((t) => t.number === user.number)
      );
    } catch (err) {
      // handel error
      console.log('get user list error');
    }
  }

  // Getter
  getUserId(): string | null {
    if (!this.user) {
      // TODO: there is no user manage it
      return null;
    }
    return this.user.sub || null;
  }

  // Public
  public async getUsers() {
    await this.getUserListApi();
    await this.populateUserDepartment();
  }
  public async populateUserDepartment(updateDepartments?: boolean) {
    // get all departments
    if (updateDepartments) await useSettingsDepartment.getDepartmentListApi();

    // clear the department name
    this.userList = this.userList.map((user) => ({
      ...user,
      departmentName: '',
    }));

    // find and populate users
    useSettingsDepartment.departmentList.forEach((dep) => {
      dep.members.forEach((mem) => {
        const userExits = this.userList.find(
          (user) => user.number === mem.phone
        );
        if (userExits) {
          userExits.departmentName = dep.department.name;
        }
      });
    });
  }
  public async initUserData() {
    // get data from token
    this.user = parseJwtPayload();

    if (this.user.adsub) this.isOwner = false;
  }
  public async updateUser(newUser: IUserTokenData) {
    this.user = { ...newUser };
  }
  public async getUserMainUuidApi(): Promise<string | null> {
    const jwtData = parseJwtPayload();

    // admin uuid is ok no need to byId from api
    if (!jwtData.adsub) {
      return jwtData.sub;
    }

    try {
      const response = await this.get<{ uuid: string }>(
        FetchingServices.ipbx,
        'nnava-number/engaged/uuid/' + jwtData.sub
      );

      return response?.data?.uuid || null;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  requireUser(user: IUserTokenData | undefined): user is IUserTokenData {
    if (!user) {
      console.error('user is not found!');
      return false;
    }
    return true;
  }
}
export const useSettingsUser = reactive(new User());
